import { apiClient, apiGet, apiPost } from '@/api/client';
import type {
  UserSession,
  LoginRequest,
  LoginResponse,
} from '@/types/auth';

/**
 * Legacy /api/Account/* endpoints — still used by the dev-stub auth flow.
 * Kept for backwards compatibility while the app migrates to the new
 * Phase 2A /api/auth/* contract below.
 */
export const authApi = {
  login(data: LoginRequest): Promise<UserSession> {
    return apiPost<UserSession>('/Account/login', data);
  },

  logout(): Promise<void> {
    return apiPost<void>('/Account/logout');
  },

  getSession(): Promise<UserSession> {
    return apiGet<UserSession>('/Account/user-preload-data');
  },

  getUserRole(): Promise<string> {
    return apiGet<string>('/Account/getUserRole');
  },

  getPermissions(): Promise<string[]> {
    return apiGet<string[]>('/Account/getpermissions');
  },

  checkSession(): Promise<boolean> {
    return apiGet<boolean>('/Account/check-if-expired-session');
  },

  forgotPassword(email: string): Promise<void> {
    return apiGet<void>(`/Account/request-forgot-password/${encodeURIComponent(email)}`);
  },

  updatePassword(email: string, data: { password: string }): Promise<void> {
    return apiPost<void>(`/Account/update-password/${encodeURIComponent(email)}`, data);
  },
};

// ---------------------------------------------------------------------------
// Phase 2A: new /api/auth/* endpoints (JWT + refresh + MFA + SSO)
// ---------------------------------------------------------------------------

export interface MfaVerifyRequest {
  code: string;
  mfaToken: string;
}

export interface PasswordResetRequestPayload {
  email: string;
}

export type ForgotPasswordResponse =
  | { ok: true; next: 'register'; email: string }
  | { ok: true; next: 'claim'; email: string }
  | { ok: true; next: 'invite_expired'; email: string }
  | {
      ok: true;
      next: 'check_email';
      devResetUrl?: string;
      devTemporaryPassword?: string;
    };

export interface PasswordResetConsumePayload {
  token: string;
  newPassword: string;
}

/** DEF-292 — post-reset: session, MFA challenge, or plain ok (mint failed). */
export type PasswordResetResult =
  | {
      ok: true;
      requiresMfa: false;
      accessToken: string;
      refreshToken: string;
      accessTokenExpiresIn?: number;
      refreshTokenExpiresAt?: string;
      user?: LoginResponse['user'];
    }
  | {
      ok: true;
      requiresMfa: true;
      mfaChallengeToken: string;
      userId: number;
      user: { userId: number; email: string };
      availableChannels: Array<'sms' | 'email'>;
      phoneMasked: string | null;
      channel?: 'sms' | 'email';
      devSecret?: string;
    }
  | { ok: true };

export interface ChangePasswordPayload {
  /** Required unless SSI is impersonating the account. */
  currentPassword?: string;
  newPassword: string;
}

/** DEF-292 — forced rotate may return a minted session or MFA challenge. */
export type ChangePasswordResponse = {
  ok: true;
  requiresMfa?: boolean;
  mfaChallengeToken?: string;
  userId?: number;
  accessToken?: string;
  refreshToken?: string;
  accessTokenExpiresIn?: number;
  refreshTokenExpiresAt?: string;
  user?: LoginResponse['user'];
  availableChannels?: Array<'sms' | 'email'>;
  phoneMasked?: string | null;
  channel?: 'sms' | 'email';
  devSecret?: string;
};

export interface MfaEnrollResponse {
  secret: string;
  otpauthUrl: string;
  qrCode: string;
}

export interface SsoStatus {
  enabled: boolean;
  provider?: string;
}

export const authApiV2 = {
  /**
   * Public SSI domain allowlist for login routing / impersonation parking.
   * Returns lowercase domains without `@` (e.g. `stevensonsystems.com`).
   */
  getInternalDomains(): Promise<{ domains: string[] }> {
    return apiGet<{ domains: string[] }>('/auth/internal-domains');
  },

  login(data: LoginRequest): Promise<LoginResponse> {
    return apiPost<LoginResponse>('/auth/login', data);
  },

  refresh(refreshToken: string): Promise<{ accessToken: string; refreshToken: string }> {
    return apiPost('/auth/refresh', { refreshToken });
  },

  logout(refreshToken: string, options?: { clearRememberMe?: boolean }): Promise<void> {
    const body: { refreshToken: string; clearRememberMe?: boolean } = { refreshToken };
    // Only send clearRememberMe when true — normal sign-out must preserve tsr_rm (AC-MFAC-006).
    if (options?.clearRememberMe) {
      body.clearRememberMe = true;
    }
    return apiPost<void>('/auth/logout', body);
  },

  logoutAll(): Promise<void> {
    return apiPost<void>('/auth/logout-all');
  },

  me(): Promise<UserSession> {
    return apiGet<UserSession>('/auth/me');
  },

  /** Post-OAuth link — maps the Supabase auth UUID onto the legacy user row. */
  linkSsoSession(): Promise<{ linked: boolean; alreadyLinked: boolean; userId: number }> {
    return apiPost('/auth/sso/link', {});
  },

  /**
   * Silent re-login from the 30-day remember-me cookie.
   * Returns a fresh session, or `null` when there is nothing to resume
   * (user did not tick Remember me / cookie expired) — server answers 204.
   */
  async resumeRememberMe(): Promise<LoginResponse | null> {
    const response = await apiClient.post('/auth/remember-me/resume', {});
    if (response.status === 204) return null;
    const data = response.data as { result?: LoginResponse } | LoginResponse | '';
    if (!data || typeof data !== 'object') return null;
    if ('result' in data) return data.result ?? null;
    return data as LoginResponse;
  },

  forgotPassword(data: PasswordResetRequestPayload): Promise<ForgotPasswordResponse> {
    return apiPost<ForgotPasswordResponse>('/auth/password/forgot', data);
  },

  resetPassword(data: PasswordResetConsumePayload): Promise<PasswordResetResult> {
    return apiPost<PasswordResetResult>('/auth/password/reset', data);
  },

  checkEmail(data: { email: string }) {
    return registerApi.checkEmail(data);
  },

  register(
    data: Parameters<typeof registerApi.register>[0],
  ) {
    return registerApi.register(data);
  },

  registerComplete(
    data: Parameters<typeof registerApi.complete>[0],
    options?: Parameters<typeof registerApi.complete>[1],
  ) {
    return registerApi.complete(data, options);
  },

  registerSendCode(data: Parameters<typeof registerApi.sendCode>[0]) {
    return registerApi.sendCode(data);
  },

  /**
   * US-DVU-08 — start DV claim completion (OTP + name + password).
   * Triggered only after login returns `claim_required`.
   */
  startDvClaim(data: {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    termsAccepted: boolean;
  }): Promise<{
    requiresMfa: true;
    registrationToken: string;
    mfaChallengeToken: string;
    email: string;
    availableChannels: Array<'sms' | 'email'>;
    codeSent: boolean;
    channel?: 'sms' | 'email';
    phoneMasked: string | null;
    devSecret?: string;
  }> {
    return apiPost('/auth/claim/start', data);
  },

  /**
   * Record the forced Terms of Service + Privacy Notice acceptance.
   * Returns the refreshed session so the blocking dialog can close on the
   * response instead of waiting for the next `/auth/me` poll.
   */
  acceptLegal(data: {
    termsAccepted: boolean;
    privacyAccepted: boolean;
  }): Promise<UserSession> {
    return apiPost<UserSession>('/auth/legal/accept', data);
  },

  /** FR-23 — one-time name capture after DV fast-path upgrade. */
  completeClaimName(data: {
    firstName: string;
    lastName: string;
  }): Promise<{ firstName: string; lastName: string; mustCompleteName: false }> {
    return apiPost('/auth/claim/complete-name', data);
  },

  changePassword(data: ChangePasswordPayload): Promise<ChangePasswordResponse> {
    return apiPost<ChangePasswordResponse>('/auth/password/change', data);
  },

  mfaEnroll(): Promise<MfaEnrollResponse> {
    return apiPost<MfaEnrollResponse>('/auth/mfa/enroll');
  },

  mfaVerify(data: MfaVerifyRequest): Promise<LoginResponse> {
    return apiPost<LoginResponse>('/auth/mfa/verify', data);
  },

  /**
   * Send / resend a login-time SMS or email OTP for a paused MFA challenge.
   */
  mfaChallengeSend(data: {
    mfaChallengeToken: string;
    channel?: 'sms' | 'email';
  }): Promise<{
    channel: 'sms' | 'email';
    availableChannels: Array<'sms' | 'email'>;
    expiresInSeconds: number;
    phoneMasked: string | null;
    mfaChallengeToken: string;
    devSecret?: string;
  }> {
    return apiPost('/auth/mfa/challenge/send', data);
  },

  /**
   * Completes a login paused on the MFA gate with a 6-digit OTP.
   */
  mfaChallengeVerify(data: import('@/types/auth').MfaChallengeVerifyRequest): Promise<LoginResponse> {
    return apiPost<LoginResponse>('/auth/mfa/challenge/verify', data);
  },

  /** DEF-241 — enumeration-safe reset-token pre-check. */
  verifyResetToken(data: { token: string }): Promise<{ valid: boolean }> {
    return apiPost<{ valid: boolean }>('/auth/password/reset/verify', data);
  },
};

// PRD §11/07 — Impersonation
export interface ImpersonationStartRequest {
  targetEmail: string;
  reason?: string;
}

export interface ImpersonationStartResponse extends LoginResponse {
  /** SSI user who initiated the impersonation. */
  realUser: {
    userId: number;
    email: string;
    role: string;
  };
}

export const impersonationApi = {
  /** Start a new impersonation session as the target user. Requires SSI auth. */
  start(data: ImpersonationStartRequest): Promise<ImpersonationStartResponse> {
    return apiPost<ImpersonationStartResponse>('/auth/impersonation/start', data);
  },

  /** Exit impersonation; client must clear tokens after this resolves. */
  async exit(refreshToken: string | null, realUserId: number | null): Promise<void> {
    // The realUserId rides in a header rather than the body so the
    // server endpoint can stay schema-compatible with /logout.
    await apiClient.post(
      '/auth/impersonation/exit',
      refreshToken ? { refreshToken } : {},
      realUserId
        ? { headers: { 'X-Impersonation-Real-User-Id': String(realUserId) } }
        : undefined,
    );
  },
};

/** PRD §10/05 — Active Sessions row returned by GET /api/auth/sessions. */
export interface ActiveSession {
  sessionId: string;
  userAgent: string | null;
  ip: string | null;
  issuedAt: string;
  expiresAt: string;
  isCurrent: boolean;
}

function refreshTokenHeader(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const refreshToken = window.localStorage.getItem('truspace.refreshToken');
  return refreshToken ? { 'X-Refresh-Token': refreshToken } : {};
}

export const sessionsApi = {
  /** PRD §10/05 AC-SES-001 — list active sessions for the caller */
  async list(): Promise<ActiveSession[]> {
    const res = await apiClient.get<{ result: { sessions: ActiveSession[] } }>(
      '/auth/sessions',
      { headers: refreshTokenHeader() },
    );
    return res.data.result.sessions;
  },

  /** PRD §10/05 AC-SES-002 — revoke a single session */
  async revoke(sessionId: string): Promise<void> {
    await apiClient.delete(`/auth/sessions/${encodeURIComponent(sessionId)}`);
  },

  /** PRD §10/05 AC-SES-003/004 — sign out everywhere (keep current optional) */
  async revokeAll(opts: { keepCurrent: boolean }): Promise<{ keptCurrent: boolean }> {
    const res = await apiClient.post<{ result: { keptCurrent: boolean } }>(
      '/auth/sessions/revoke-all',
      { keepCurrent: opts.keepCurrent },
      { headers: refreshTokenHeader() },
    );
    return res.data.result;
  },
};

export const ssoApi = {
  status(): Promise<SsoStatus> {
    return apiGet<SsoStatus>('/sso/status');
  },

  startUrl(returnTo?: string): string {
    const qs = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
    return `/api/sso/start${qs}`;
  },
};

export const registerApi = {
  checkEmail(data: { email: string }): Promise<
    { canRegister: true; email: string } | { canRegister: false; message: string }
  > {
    return apiPost('/auth/register/check-email', data);
  },

  register(data: {
    email: string;
    firstName: string;
    lastName: string;
    companyName: string;
    workPhone: string;
    authPhone: string;
    password: string;
    termsAccepted: boolean;
  }): Promise<{
    requiresMfa: true;
    registrationToken: string;
    mfaChallengeToken: string;
    email: string;
    availableChannels: Array<'sms' | 'email'>;
    codeSent: boolean;
    channel?: 'sms' | 'email';
    phoneMasked: string | null;
    devSecret?: string;
  }> {
    return apiPost('/auth/register', data);
  },

  sendCode(data: {
    registrationToken: string;
    mfaChallengeToken: string;
    channel?: 'sms' | 'email';
  }): Promise<{
    channel: 'sms' | 'email';
    availableChannels: Array<'sms' | 'email'>;
    phoneMasked: string | null;
    email: string;
    mfaChallengeToken: string;
    devSecret?: string;
  }> {
    return apiPost('/auth/register/send-code', data);
  },

  complete(
    data: {
      registrationToken: string;
      mfaChallengeToken: string;
      code: string;
    },
    options?: { idempotencyKey?: string },
  ): Promise<
    LoginResponse & {
      sessionPending?: boolean;
    }
  > {
    // DEF-229 — never leave the OTP screen spinning forever if the server
    // hangs. Server complete is budgeted well under this; FE aborts as a backstop.
    return apiPost('/auth/register/complete', data, {
      timeout: 8_000,
      ...(options?.idempotencyKey
        ? { headers: { 'Idempotency-Key': options.idempotencyKey } }
        : {}),
    });
  },
};

/** @deprecated Use registerApi — kept as alias during Auth V3 cutover. */
export const authRegisterApi = registerApi;
